import { getActiveParkingId } from "../../utils/tenant";
import React, { useEffect, useState, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mn from "date-fns/locale/mn";
import { getAuth, signOut } from "firebase/auth";
import { 
    getFirestore, collection, query, where, getDocs, doc, setDoc, updateDoc, onSnapshot, writeBatch
} from "firebase/firestore";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    FiClock, FiLogOut, FiSearch, FiCalendar, 
    FiUsers, FiUserCheck, FiUserX, FiAlertCircle, 
    FiEye, FiX, FiCheckCircle, FiEdit2, FiTrendingUp, FiAlertTriangle, FiFileText, FiInfo, FiDatabase, FiPhone, FiMail
} from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';

registerLocale("mn", mn);
const MySwal = withReactContent(Swal);

// MINIMAL TOAST NOTIFICATION
const Toast = MySwal.mixin({
    toast: true, position: 'top-end', showConfirmButton: false, timer: 2500, timerProgressBar: true, iconColor: '#10b981',
    customClass: { popup: 'tw-bg-white tw-rounded-xl tw-shadow-lg tw-border tw-border-slate-100 tw-px-4 tw-py-3', title: 'tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0', timerProgressBar: 'tw-bg-slate-200' }
});

export default function HrDashboard() {
    const [employees, setEmployees] = useState([]);
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [historicalAtt, setHistoricalAtt] = useState([]);
    
    // Статистикууд
    const [totalEmployees, setTotalEmployees] = useState(0); 
    const [presentToday, setPresentToday] = useState(0);
    const [lateCount, setLateCount] = useState(0);
    const [absentCount, setAbsentCount] = useState(0);
    const [overtimeCount, setOvertimeCount] = useState(0);

    // Чөлөө, Амралт, Томилолт тус бүрийн тоолуур
    const [leaveCount, setLeaveCount] = useState(0); 
    const [annualLeaveCount, setAnnualLeaveCount] = useState(0);
    const [businessTripCount, setBusinessTripCount] = useState(0);
    const [sickCount, setSickCount] = useState(0);

    const [missingCheckout, setMissingCheckout] = useState([]);
    const [chronicLate, setChronicLate] = useState([]);

    const [searchInput, setSearchInput] = useState(""); 
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState(new Date()); 
    const [statusFilter, setStatusFilter] = useState("all"); 
    const [isLoadingData, setIsLoadingData] = useState(true); 
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Системийн дүрмүүд болон Баярын өдрүүдийг хадгалах
    const [systemRules, setSystemRules] = useState({ gracePeriod: 15, overtimeThreshold: 60 });
    const [holidays, setHolidays] = useState([]);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeAttendance, setEmployeeAttendance] = useState([]);
    const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editStatusValue, setEditStatusValue] = useState("ontime");

    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [leaveForm, setLeaveForm] = useState({
        emp_id: "", startDate: "", endDate: "", leaveType: "leave", reason: ""
    });

    const [isGeneratingDummy, setIsGeneratingDummy] = useState(false);

    const db = getFirestore(); 
    const auth = getAuth();
    const pageSize = 50;

    useEffect(() => {
        const timer = setTimeout(() => { setSearchQuery(searchInput); setCurrentPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleLogout = async () => {
        try { await signOut(auth); } 
        catch (error) { Toast.fire({ icon: 'error', title: 'Гарахад алдаа гарлаа' }); }
    };
    
    const dateToString = (d) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    };

    const parseToDate = (t) => {
        if (!t) return null;
        return typeof t.toDate === 'function' ? t.toDate() : new Date(t);
    };

    // ⭐️ ЦАГ 24 ЦАГИЙН ФОРМАТ РУУ ШИЛЖҮҮЛСЭН
    const formatTime = (timestamp) => {
        const dateObj = parseToDate(timestamp);
        if (!dateObj) return "-";
        return dateObj.toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    };

    const formatTimeOnly = (t) => {
        const parsed = parseToDate(t);
        if(!parsed) return "-";
        return parsed.toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    useEffect(() => {
        const unsubRules = onSnapshot(doc(db, "parkings", getActiveParkingId(), "settings", "general"), doc => {
            if (doc.exists()) setSystemRules(doc.data());
        });
        const unsubHols = onSnapshot(collection(db, "parkings", getActiveParkingId(), "holidays"), snap => {
            setHolidays(snap.docs.map(d => d.data().date));
        });
        return () => { unsubRules(); unsubHols(); };
    }, [db]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setIsLoadingData(true);
            try {
                const targetDateStr = dateToString(selectedDate);
                const startDate = new Date(selectedDate);
                startDate.setDate(startDate.getDate() - 6);
                const startDateStr = dateToString(startDate);

                const yesterday = new Date(selectedDate);
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = dateToString(yesterday);

                const empRef = collection(db, "parkings", getActiveParkingId(), "employees");
                const empSnap = await getDocs(empRef);
                const allEmps = empSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setEmployees(allEmps);

                const attRef = collection(db, "parkings", getActiveParkingId(), "daily_attendance");
                const qAtt = query(attRef, where("date", ">=", startDateStr), where("date", "<=", targetDateStr));
                const attSnap = await getDocs(qAtt);
                const allAttLogs = attSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setHistoricalAtt(allAttLogs);

                const todayLogs = allAttLogs.filter(a => a.date === targetDateStr);
                const yesterdayLogs = allAttLogs.filter(a => a.date === yesterdayStr);

                let present = 0, late = 0, absent = 0, overtime = 0;
                let leave = 0, annualLeave = 0, businessTrip = 0, sick = 0;

                const isHoliday = holidays.includes(targetDateStr); 

                const mergedLogs = allEmps.map(emp => {
                    const record = todayLogs.find(a => a.emp_id === emp.emp_id);
                    const fullName = `${emp.lastName || ''} ${emp.firstName || emp.name || ''}`.trim();
                    const scheduleStr = emp.workSchedule || "08:00 - 17:00";
                    
                    let expectInTotalMins = 480;  
                    let expectOutTotalMins = 1020; 
                    let expectedWorkMins = 480;   

                    if (scheduleStr.includes("-")) {
                        const [startStr, endStr] = scheduleStr.split("-").map(s => s.trim());
                        if (startStr.includes(":") && endStr.includes(":")) {
                            const [expectInHr, expectInMin] = startStr.split(":").map(Number);
                            const [expectOutHr, expectOutMin] = endStr.split(":").map(Number);
                            
                            expectInTotalMins = (expectInHr || 8) * 60 + (expectInMin || 0);
                            expectOutTotalMins = (expectOutHr || 17) * 60 + (expectOutMin || 0);
                            expectedWorkMins = expectOutTotalMins - expectInTotalMins;
                            if (expectedWorkMins < 0) expectedWorkMins += 24 * 60; 
                        }
                    }

                    if (record) {
                        if (record.status === 'ontime' || record.status === 'late') {
                            if (record.first_in) {
                                const inTime = parseToDate(record.first_in);
                                const inTotalMins = inTime.getHours() * 60 + inTime.getMinutes();
                                const allowedMins = expectInTotalMins + (Number(systemRules.gracePeriod) || 0);

                                if (inTotalMins > allowedMins) {
                                    record.status = 'late';
                                } else {
                                    record.status = 'ontime';
                                }
                            }
                        }

                        if (record.status === 'ontime') present++;
                        else if (record.status === 'late') late++;
                        else if (record.status === 'absent') absent++; 
                        else if (record.status === 'leave') leave++;
                        else if (record.status === 'annual_leave') annualLeave++;
                        else if (record.status === 'business_trip') businessTrip++;
                        else if (record.status === 'sick') sick++;

                        let duration = "-";
                        let actualHrs = 0;

                        if (record.first_in && record.last_out) {
                            const start = parseToDate(record.first_in);
                            const end = parseToDate(record.last_out);
                            const diffMins = Math.floor((end - start) / 60000);
                            
                            actualHrs = Math.floor(diffMins / 60);
                            duration = `${actualHrs}ц ${diffMins % 60}м`;
                            
                            const extraMins = diffMins - expectedWorkMins;
                            if(extraMins >= (Number(systemRules.overtimeThreshold) || 60)) overtime++; 
                        }

                        return {
                            ...emp, name: fullName, schedule: scheduleStr,
                            first_in: record.first_in, last_out: record.last_out,
                            duration: duration, hrsWorked: actualHrs, status: record.status, logId: record.id,
                            reason: record.reason || "" 
                        };
                    } else {
                        if (isHoliday) {
                            return { ...emp, name: fullName, schedule: scheduleStr, first_in: null, last_out: null, duration: "-", hrsWorked: 0, status: "holiday", logId: null, reason: "Нийтээр амрах баярын өдөр" };
                        } else {
                            absent++;
                            return { ...emp, name: fullName, schedule: scheduleStr, first_in: null, last_out: null, duration: "-", hrsWorked: 0, status: "absent", logId: null, reason: "" };
                        }
                    }
                });

                const noCheckout = yesterdayLogs.filter(l => l.first_in && !l.last_out).map(l => {
                    const e = allEmps.find(emp => emp.emp_id === l.emp_id);
                    return e ? `${e.lastName?.charAt(0)}. ${e.firstName || e.name}` : l.emp_id;
                });
                setMissingCheckout(noCheckout);

                const lateCounts = {};
                allAttLogs.forEach(l => { if(l.status === 'late') lateCounts[l.emp_id] = (lateCounts[l.emp_id] || 0) + 1; });
                const chronic = Object.keys(lateCounts).filter(id => lateCounts[id] >= 3).map(id => {
                    const e = allEmps.find(emp => emp.emp_id === id);
                    return e ? `${e.lastName?.charAt(0)}. ${e.firstName || e.name}` : id;
                });
                setChronicLate(chronic);

                setTotalEmployees(allEmps.length); setPresentToday(present); setLateCount(late); setAbsentCount(absent);
                setLeaveCount(leave); setAnnualLeaveCount(annualLeave); setBusinessTripCount(businessTrip); setSickCount(sick); setOvertimeCount(overtime);
                
                mergedLogs.sort((a, b) => {
                    if(!a.first_in) return 1;
                    if(!b.first_in) return -1;
                    return parseToDate(b.first_in) - parseToDate(a.first_in); 
                });
                setAttendanceLogs(mergedLogs);

            } catch (error) { 
                console.error("Dashboard Дата татах алдаа:", error); 
            } finally { 
                setIsLoadingData(false); 
            }
        };

        if (holidays.length >= 0) fetchDashboardData(); 
    }, [db, selectedDate, refreshTrigger, holidays, systemRules]); 

    const handleGenerateDummyAttendance = async () => {
    setIsGeneratingDummy(true);
    try {
        if (employees.length === 0) {
            Toast.fire({ icon: 'warning', title: 'Эхлээд ажилтан бүртгэнэ үү!' });
            setIsGeneratingDummy(false);
            return;
        }

        // 1. Batch үүсгэх
        let batch = writeBatch(db);
        let operationCount = 0;

        for (let i = 0; i <= 6; i++) {
            const d = new Date(selectedDate);
            d.setDate(d.getDate() - i);
            
            // Нэмэлт: Бямба (6), Ням (0) гаригт дата үүсгэхгүй алгасах (Хэрэв хүсвэл)
            const dayOfWeek = d.getDay();
            if (dayOfWeek === 0 || dayOfWeek === 6) continue; 

            const dateStr = dateToString(d);

            for (const emp of employees) {
                const docId = `${dateStr}_${emp.emp_id}`;
                const docRef = doc(db, "parkings", getActiveParkingId(), "daily_attendance", docId);
                
                const rand = Math.random();
                let status = 'ontime';
                let firstIn = null;
                let lastOut = null;

                if (rand < 0.7) {
                    status = 'ontime';
                    firstIn = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 7, Math.floor(Math.random() * 20) + 40); 
                    lastOut = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 17, Math.floor(Math.random() * 30)); 
                } else if (rand < 0.85) {
                    status = 'late';
                    firstIn = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 8, Math.floor(Math.random() * 45) + 15); 
                    lastOut = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 17, Math.floor(Math.random() * 30)); 
                } else if (rand < 0.9) {
                    status = 'absent';
                } else {
                    status = 'leave'; 
                }

                // 2. setDoc-ийн оронд batch.set ашиглах (await хүлээхгүйгээр санах ойд бүртгэнэ)
                batch.set(docRef, {
                    emp_id: emp.emp_id,
                    date: dateStr,
                    first_in: firstIn,
                    last_out: lastOut,
                    status: status,
                    reason: status === 'leave' ? 'Ээлжийн амралттай' : '',
                    scans: [],
                    updatedAt: new Date()
                }, { merge: true });

                operationCount++;

                // Firebase Batch нь нэг удаад дээд тал нь 500 үйлдэл хийх хязгаартай тул шалгаж хуваах
                if (operationCount >= 450) {
                    await batch.commit(); // Цуглуулсан датагаа бааз руу илгээх
                    batch = writeBatch(db); // Дараагийн 500-д зориулж шинээр үүсгэх
                    operationCount = 0;
                }
            }
        }
        
        // 3. Үлдсэн багц датаг илгээх
        if (operationCount > 0) {
            await batch.commit();
        }

        Toast.fire({ icon: 'success', title: 'Туршилтын ирц амжилттай үүслээ' });
        setRefreshTrigger(prev => prev + 1);
    } catch (error) {
        console.error("Тест дата үүсгэх үед алдаа гарлаа:", error);
        Toast.fire({ icon: 'error', title: 'Алдаа гарлаа' });
    } finally {
        setIsGeneratingDummy(false);
    }
};

    const handleSaveLeave = async (e) => {
        e.preventDefault();
        if (!leaveForm.emp_id || !leaveForm.startDate || !leaveForm.endDate) {
            return Toast.fire({ icon: 'warning', title: 'Ажилтан болон огноог сонгоно уу!' });
        }
        const start = new Date(leaveForm.startDate);
        const end = new Date(leaveForm.endDate);
        if (start > end) {
            return Toast.fire({ icon: 'error', title: 'Дуусах огноо эхлэх огнооноос өмнө байна!' });
        }

        setIsSaving(true);
        try {
            let currentDate = new Date(start);
            while (currentDate <= end) {
                const dStr = dateToString(currentDate);
                const docId = `${dStr}_${leaveForm.emp_id}`;
                const docRef = doc(db, "parkings", getActiveParkingId(), "daily_attendance", docId);
                
                await setDoc(docRef, {
                    emp_id: leaveForm.emp_id, date: dStr, status: leaveForm.leaveType, reason: leaveForm.reason || "", updatedAt: new Date()
                }, { merge: true });

                currentDate.setDate(currentDate.getDate() + 1);
            }
            Toast.fire({ icon: 'success', title: 'Амжилттай бүртгэгдлээ!' });
            setIsLeaveModalOpen(false);
            setLeaveForm({ emp_id: "", startDate: "", endDate: "", leaveType: "leave", reason: "" });
            setRefreshTrigger(prev => prev + 1); 
        } catch (error) {
            Toast.fire({ icon: 'error', title: 'Бүртгэхэд алдаа гарлаа.' });
        } finally { setIsSaving(false); }
    };

    const handleSaveStatus = async () => {
        setIsSaving(true);
        try {
            const targetDateStr = dateToString(selectedDate);
            const docId = `${targetDateStr}_${selectedEmployee.emp_id}`;
            const docRef = doc(db, "parkings", getActiveParkingId(), "daily_attendance", docId);
            
            if (selectedEmployee.logId) {
                await updateDoc(docRef, { status: editStatusValue, updatedAt: new Date() });
            } else {
                await setDoc(docRef, {
                    emp_id: selectedEmployee.emp_id, date: targetDateStr,
                    first_in: null, last_out: null, status: editStatusValue, scans: [], createdAt: new Date()
                });
            }
            Toast.fire({ icon: 'success', title: 'Төлөв өөрчлөгдлөө' });
            setIsEditModalOpen(false);
            setRefreshTrigger(prev => prev + 1);
        } catch (e) { 
            Toast.fire({ icon: 'error', title: 'Төлөв засаж чадсангүй.' }); 
        } finally { setIsSaving(false); }
    };

    const handleShowReason = (log) => {
        const statusInfo = getStatusUI(log.status);
        MySwal.fire({
            title: statusInfo.text,
            html: `<div class="tw-text-sm tw-text-slate-600 tw-mt-2">
                     <b>Ажилтан:</b> ${log.name} <br/>
                     <b>Огноо:</b> ${log.date || dateToString(selectedDate)} <br/>
                     <div class="tw-mt-3 tw-p-3 tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-200">
                        <b>Шалтгаан:</b> ${log.reason ? log.reason : '<span class="tw-text-slate-400 tw-italic">Тайлбар бичээгүй байна.</span>'}
                     </div>
                   </div>`,
            icon: 'info',
            confirmButtonText: 'Хаах',
            confirmButtonColor: '#3b82f6',
            customClass: { popup: 'tw-rounded-2xl tw-p-5 tw-shadow-2xl', confirmButton: 'tw-rounded-xl tw-w-full' }
        });
    };

    const departmentData = useMemo(() => {
        const deptMap = {};
        attendanceLogs.forEach(log => {
            const d = log.department || "Бусад";
            if(!deptMap[d]) deptMap[d] = { name: d, 'Хэвийн': 0, 'Хоцорсон': 0, 'Ажилд ирээгүй': 0, 'Чөлөө/Бусад': 0 };
            
            if(log.status === 'ontime') deptMap[d]['Хэвийн']++;
            else if(log.status === 'late') deptMap[d]['Хоцорсон']++;
            else if(log.status === 'absent') deptMap[d]['Ажилд ирээгүй']++;
            else deptMap[d]['Чөлөө/Бусад']++; 
        });
        return Object.values(deptMap).map(d => ({ ...d, name: d.name.split(" ")[0] }));
    }, [attendanceLogs]);

    const trendData = useMemo(() => {
        const trends = {};
        for(let i=6; i>=0; i--) {
            const d = new Date(selectedDate);
            d.setDate(d.getDate() - i);
            const dStr = dateToString(d);
            trends[dStr] = { date: d.toLocaleDateString('mn-MN', { weekday: 'short' }), 'Ирсэн хүн': 0 };
        }
        historicalAtt.forEach(log => {
            if(trends[log.date] && log.status !== 'absent' && log.status !== 'holiday') {
                trends[log.date]['Ирсэн хүн']++;
            }
        });
        return Object.values(trends);
    }, [historicalAtt, selectedDate]);

    const liveFeed = useMemo(() => attendanceLogs.filter(l => l.first_in).slice(0, 10), [attendanceLogs]);

    const handleViewClick = async (emp) => {
        setSelectedEmployee(emp); setIsViewModalOpen(true); setIsLoadingAttendance(true); setEmployeeAttendance([]); 
        try {
            const q = query(collection(db, "parkings", getActiveParkingId(), "daily_attendance"), where("emp_id", "==", emp.emp_id));
            const querySnapshot = await getDocs(q);
            const attData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const sortedData = attData.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 7);
            setEmployeeAttendance(sortedData);
        } catch (error) { console.error("Алдаа: ", error); } 
        finally { setIsLoadingAttendance(false); }
    };

    const handleEditClick = (emp) => {
        setSelectedEmployee(emp);
        setEditStatusValue(emp.status === 'absent' ? 'leave' : emp.status);
        setIsEditModalOpen(true);
    };

    const [isSaving, setIsSaving] = useState(false);

    const filteredLogs = useMemo(() => {
        let result = attendanceLogs;
        if (statusFilter !== "all") {
            result = result.filter(log => log.status === statusFilter);
        }
        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            result = result.filter(log => String(log.name || "").toLowerCase().includes(queryLower) || String(log.emp_id || "").toLowerCase().includes(queryLower));
        }
        return result;
    }, [attendanceLogs, searchQuery, statusFilter]); 

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const getStatusUI = (status) => {
        switch(status) {
            case 'ontime': return { color: "tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200", text: "Хэвийн" };
            case 'late': return { color: "tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200", text: "Хоцорсон" };
            case 'leave': return { color: "tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200", text: "Чөлөө" };
            case 'annual_leave': return { color: "tw-bg-teal-50 tw-text-teal-700 tw-border-teal-200", text: "Ээлжийн амралт" };
            case 'business_trip': return { color: "tw-bg-indigo-50 tw-text-indigo-700 tw-border-indigo-200", text: "Томилолт" };
            case 'sick': return { color: "tw-bg-purple-50 tw-text-purple-700 tw-border-purple-200", text: "Өвчтэй" };
            case 'absent': return { color: "tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200", text: "Ажилд ирээгүй" };
            case 'holiday': return { color: "tw-bg-rose-50 tw-text-rose-600 tw-border-rose-200", text: "Баяр / Амралт" };
            default: return { color: "tw-bg-slate-100 tw-text-slate-600 tw-border-slate-200", text: "-" };
        }
    };

    return (
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-3 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800 tw-max-w-[100vw] tw-overflow-x-hidden">
            
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-mb-6 tw-gap-4">
                <div>
                    <h2 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-m-0">Хүний Нөөцийн Хяналт</h2>
                    <p className="tw-text-xs sm:tw-text-sm tw-text-slate-50 tw-mt-1 tw-mb-0">Ажилтнуудын цаг бүртгэлийн нэгдсэн систем</p>
                </div>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 sm:tw-gap-3">
                    
                    {/* <button onClick={handleGenerateDummyAttendance} disabled={isGeneratingDummy} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-amber-100 hover:tw-bg-amber-200 tw-text-amber-700 tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-sm tw-transition-colors">
                        <FiDatabase size={16} /> {isGeneratingDummy ? 'Үүсгэж байна...' : 'Ирц тестлэх дата үүсгэх'}
                    </button> */}

                    <button onClick={() => setIsLeaveModalOpen(true)} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-indigo-600 hover:tw-bg-indigo-700 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-sm tw-transition-colors">
                        <FiFileText size={16} /> Чөлөө бүртгэх
                    </button>

                    <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-sm tw-px-3 tw-py-2">
                        <FiCalendar className="tw-text-slate-400 tw-mr-2" size={18} />
                        <DatePicker selected={selectedDate} onChange={(date) => { setSelectedDate(date); setCurrentPage(1); }} dateFormat="yyyy-MM-dd" maxDate={new Date()} locale="mn" popperPlacement="bottom-end" className="tw-bg-transparent tw-text-sm tw-font-bold tw-text-slate-700 tw-outline-none tw-w-28 tw-cursor-pointer" />
                    </div>
                    <button onClick={handleLogout} className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-slate-200 tw-text-slate-600 tw-font-bold tw-text-sm tw-rounded-xl hover:tw-bg-slate-50"><FiLogOut size={16}/></button>
                </div>
            </div>

            {/* SMART ALERTS */}
            {(missingCheckout.length > 0 || chronicLate.length > 0) && (
                <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 tw-mb-6">
                    {missingCheckout.length > 0 && (
                        <div className="tw-flex-1 tw-bg-rose-50 tw-border tw-border-rose-200 tw-rounded-2xl tw-p-4 tw-flex tw-items-start tw-gap-3">
                            <FiAlertCircle className="tw-text-rose-500 tw-mt-0.5 tw-flex-shrink-0" size={20} />
                            <div>
                                <h4 className="tw-text-sm tw-font-bold tw-text-rose-800 tw-mb-1">Гарах цагаа уншуулаагүй ({missingCheckout.length} хүн)</h4>
                                <p className="tw-text-xs tw-text-rose-600 tw-m-0">Өчигдөр: {missingCheckout.slice(0, 5).join(", ")} {missingCheckout.length > 5 ? 'г.м' : ''}</p>
                            </div>
                        </div>
                    )}
                    {chronicLate.length > 0 && (
                        <div className="tw-flex-1 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-2xl tw-p-4 tw-flex tw-items-start tw-gap-3">
                            <FiAlertTriangle className="tw-text-amber-500 tw-mt-0.5 tw-flex-shrink-0" size={20} />
                            <div>
                                <h4 className="tw-text-sm tw-font-bold tw-text-amber-800 tw-mb-1">Архаг хоцролт /Сүүлийн 7 хоног/ ({chronicLate.length} хүн)</h4>
                                <p className="tw-text-xs tw-text-amber-600 tw-m-0">{chronicLate.slice(0, 5).join(", ")} {chronicLate.length > 5 ? 'г.м' : ''}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ⭐️ KPI STATS (5 баганатай болгосон, Илүү цаг устгагдсан) */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-4 sm:tw-gap-6 tw-mb-6">
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2"><FiUsers className="tw-text-blue-500"/><p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0 tw-whitespace-nowrap">Нийт ажилтан</p></div>
                    <h3 className="tw-text-3xl tw-font-black tw-text-slate-800">{totalEmployees}</h3> 
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2"><FiUserCheck className="tw-text-emerald-500"/><p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0 tw-whitespace-nowrap">Ирсэн</p></div>
                    <h3 className="tw-text-3xl tw-font-black tw-text-emerald-600">{presentToday}</h3>
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2"><FiAlertCircle className="tw-text-amber-500"/><p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0 tw-whitespace-nowrap">Хоцорсон</p></div>
                    <h3 className="tw-text-3xl tw-font-black tw-text-amber-600">{lateCount}</h3>
                </div>
                <div className="tw-bg-white tw-p-5 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2"><FiUserX className="tw-text-rose-500"/><p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0 tw-whitespace-nowrap">Ажилд ирээгүй</p></div>
                    <h3 className="tw-text-3xl tw-font-black tw-text-rose-600">{absentCount}</h3>
                </div>
                
                <div className="tw-bg-white tw-p-4 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-flex tw-flex-col tw-justify-center">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1"><FiFileText className="tw-text-indigo-500"/><p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-m-0 tw-whitespace-nowrap">Чөлөө / Бусад</p></div>
                    <h3 className="tw-text-3xl tw-font-black tw-text-indigo-600 tw-leading-none">{leaveCount + annualLeaveCount + businessTripCount + sickCount}</h3>
                    <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-2 tw-text-[9px] tw-font-bold tw-text-slate-500">
                        {leaveCount > 0 && <span className="tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded" title="Чөлөө">Чөлөө: {leaveCount}</span>}
                        {annualLeaveCount > 0 && <span className="tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded" title="Ээлжийн амралт">Амралт: {annualLeaveCount}</span>}
                        {businessTripCount > 0 && <span className="tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded" title="Томилолт">Томилолт: {businessTripCount}</span>}
                        {sickCount > 0 && <span className="tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded" title="Өвчтэй">Өвчтэй: {sickCount}</span>}
                        {(leaveCount + annualLeaveCount + businessTripCount + sickCount) === 0 && <span>-</span>}
                    </div>
                </div>
            </div>

            {/* GRAPHS */}
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-mb-8">
                
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-p-5 tw-flex tw-flex-col tw-h-[300px]">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                        <div className="tw-w-2 tw-h-2 tw-bg-emerald-500 tw-rounded-full tw-animate-ping"></div>
                        <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0">Live</h3>
                    </div>
                    <p className="tw-text-[10px] tw-text-slate-400 tw-mb-4 tw-leading-tight">Өнөөдөр хамгийн сүүлд ажилдаа ирсэн 10 ажилтны цагийн мэдээлэл.</p>
                    
                    <div className="tw-overflow-y-auto tw-flex-1 tw-pr-2">
                        {liveFeed.length === 0 ? <p className="tw-text-xs tw-text-slate-400 tw-text-center tw-mt-10">Мэдээлэл байхгүй</p> : 
                            liveFeed.map((log, i) => (
                                <div key={i} className="tw-flex tw-justify-between tw-items-center tw-py-2.5 tw-border-b tw-border-slate-50 last:tw-border-0">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-8 tw-h-8 tw-bg-blue-50 tw-text-blue-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-xs">
                                            {log.name?.charAt(0) || "A"}
                                        </div>
                                        <div>
                                            <p className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0 tw-leading-tight">{log.name}</p>
                                            <p className="tw-text-[10px] tw-text-slate-400 tw-m-0">{log.department}</p>
                                        </div>
                                    </div>
                                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">{formatTimeOnly(log.first_in)}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>

                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-p-5 tw-flex tw-flex-col tw-h-[320px]">
    <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0 tw-flex tw-items-center tw-gap-2">
        <FiUsers className="tw-text-indigo-500"/> Хэлтэс бүрийн ирц
    </h3>
    <p className="tw-text-[10px] tw-text-slate-400 tw-mt-1 tw-mb-2 tw-leading-tight">
        Хэлтэс тус бүрд хэвийн ирсэн, хоцорсон болон ажилд ирээгүй ажилтнуудын тоо.
    </p>
    
    {/* Өндрийг (height) шууд зааж өгснөөр график алга болох асуудал шийдэгдэнэ */}
    <div className="tw-w-full tw-h-[220px] tw-text-xs tw-mt-auto">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={departmentData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                <XAxis dataKey="name" tick={{fontSize: 10}} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{fontSize: 10}} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{fill: '#f1f5f9'}} />
                <Legend wrapperStyle={{fontSize: '11px', paddingTop: '10px'}} />
                <Bar dataKey="Хэвийн" stackId="a" fill="#10b981" barSize={30} />
                <Bar dataKey="Хоцорсон" stackId="a" fill="#f59e0b" />
                <Bar dataKey="Ажилд ирээгүй" stackId="a" fill="#f43f5e" />
                <Bar dataKey="Чөлөө/Бусад" stackId="a" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    </div>
</div>

                

                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-p-5 tw-flex tw-flex-col tw-h-[300px]">
                    <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0 tw-flex tw-items-center tw-gap-2"><FiTrendingUp className="tw-text-emerald-500"/> Ирцийн чиг хандлага</h3>
                    <p className="tw-text-[10px] tw-text-slate-400 tw-mt-1 tw-mb-4 tw-leading-tight">Сүүлийн 7 хоногт байгууллагын хэмжээнд ажилдаа ирсэн нийт хүний тоон өөрчлөлт.</p>
                    
                    <div className="tw-flex-1 tw-w-full tw-text-xs">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                                <YAxis tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                                <Tooltip />
                                <Area type="monotone" dataKey="Ирсэн хүн" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorPresent)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
            
            {/* FILTER & SEARCH */}
            <div className="tw-bg-white tw-rounded-t-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-border-b-0 tw-p-4 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-stretch lg:tw-items-center tw-gap-4">
                <div className="tw-relative tw-w-full lg:tw-w-64 tw-flex-shrink-0">
                    <FiSearch className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input 
                        type="text" placeholder="Ажилтан хайх..."
                        value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                
                <div className="tw-flex tw-bg-slate-100 tw-p-1 tw-rounded-xl tw-w-full tw-overflow-x-auto custom-scrollbar">
                    <button onClick={() => setStatusFilter('all')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'all' ? 'tw-bg-white tw-text-slate-800 tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-slate-700'}`}>Бүгд</button>
                    <button onClick={() => setStatusFilter('ontime')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'ontime' ? 'tw-bg-emerald-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-emerald-600'}`}>Хэвийн</button>
                    <button onClick={() => setStatusFilter('late')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'late' ? 'tw-bg-amber-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-amber-600'}`}>Хоцорсон</button>
                    <button onClick={() => setStatusFilter('absent')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'absent' ? 'tw-bg-rose-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-rose-600'}`}>Ажилд ирээгүй</button>
                    <button onClick={() => setStatusFilter('leave')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'leave' ? 'tw-bg-blue-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-blue-600'}`}>Чөлөө</button>
                    <button onClick={() => setStatusFilter('annual_leave')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'annual_leave' ? 'tw-bg-teal-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-teal-600'}`}>Амралт</button>
                    <button onClick={() => setStatusFilter('business_trip')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'business_trip' ? 'tw-bg-indigo-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-indigo-600'}`}>Томилолт</button>
                    <button onClick={() => setStatusFilter('sick')} className={`tw-flex-1 lg:tw-flex-none tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-transition-all tw-whitespace-nowrap ${statusFilter === 'sick' ? 'tw-bg-purple-500 tw-text-white tw-shadow-sm' : 'tw-text-slate-500 hover:tw-text-purple-600'}`}>Өвчтэй</button>
                </div>
            </div>

            {/* MAIN TABLE */}
            <div className="tw-bg-white tw-shadow-sm tw-border tw-border-slate-200 tw-rounded-b-2xl tw-w-full tw-overflow-hidden">
                <div className="tw-overflow-x-auto tw-w-full tw-block">
                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[1000px]">
                        <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
                            <tr>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest tw-w-12">№</th>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest">Ажилтны ID</th>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest">Овог Нэр</th>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest">Хэлтэс / Хуваарь</th>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest">Ирсэн</th>
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest">Гарсан</th>
                                {/* ⭐️ Ажилласан багана хасагдсан */}
                                <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-widest tw-text-center">Төлөв</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                            {isLoadingData ? (
                                <tr><td colSpan={7} className="tw-py-16 tw-text-center tw-text-blue-500 tw-font-bold">Уншиж байна...</td></tr>
                            ) : paginatedLogs.length === 0 ? (
                                <tr><td colSpan={7} className="tw-py-16 tw-text-center tw-text-slate-400">Мэдээлэл олдсонгүй.</td></tr>
                            ) : paginatedLogs.map((log, idx) => {
                                const statusUI = getStatusUI(log.status);
                                const isLeaveType = ['leave', 'annual_leave', 'sick', 'business_trip', 'holiday'].includes(log.status);

                                return (
                                    <tr key={log.id || log.emp_id} className="hover:tw-bg-slate-50 tw-transition-colors">
                                        <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-slate-400">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td className="tw-px-4 tw-py-3"><div className="tw-inline-block tw-px-2 tw-py-1 tw-bg-slate-100 tw-rounded-md tw-text-xs tw-font-bold tw-text-slate-700">{log.emp_id}</div></td>
                                        <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-slate-800">
                                            {log.name}
                                            {log.employmentType && <div className="tw-text-[10px] tw-text-indigo-500">{log.employmentType}</div>}
                                        </td>
                                        <td className="tw-px-4 tw-py-3">
                                            <div className="tw-text-xs tw-font-medium tw-text-slate-600">{log.department}</div>
                                            <div className="tw-text-[10px] tw-font-bold tw-blue-600 tw-mt-0.5">{log.position || "Ажилтан"}</div>
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-xs tw-font-bold tw-text-slate-600">{log.first_in ? formatTime(log.first_in) : "-"}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-xs tw-font-bold tw-text-slate-600">{log.last_out ? formatTime(log.last_out) : "-"}</td>
                                        {/* ⭐️ Ажилласан баганын TD хасагдсан */}
                                        <td className="tw-px-4 tw-py-3">
                                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                
                                                {isLeaveType ? (
                                                    <button onClick={() => handleShowReason(log)} className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-text-[10px] tw-font-bold tw-whitespace-nowrap hover:tw-opacity-80 ${statusUI.color}`} title="Дарж шалтгааныг харах">
                                                        {statusUI.text} <FiInfo size={12} />
                                                    </button>
                                                ) : (
                                                    <span className={`tw-px-2 tw-py-1 tw-rounded-md tw-text-[10px] tw-font-bold tw-whitespace-nowrap ${statusUI.color}`}>
                                                        {statusUI.text}
                                                    </span>
                                                )}

                                                <div className="tw-flex tw-border tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
                                                    <button onClick={() => handleEditClick(log)} className="tw-p-1.5 tw-bg-white hover:tw-bg-slate-100 tw-text-slate-400 hover:tw-text-amber-600 tw-transition-colors tw-border-r tw-border-slate-200" title="Төлөв өөрчлөх"><FiEdit2 size={14} /></button>
                                                    <button onClick={() => handleViewClick(log)} className="tw-p-1.5 tw-bg-white hover:tw-bg-slate-100 tw-text-slate-400 hover:tw-text-blue-600 tw-transition-colors" title="Дэлгэрэнгүй"><FiEye size={14} /></button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {filteredLogs.length > 0 && (
                    <div className="tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-justify-between tw-items-center">
                        <div className="tw-text-sm tw-text-slate-500">Нийт: <span className="tw-font-bold tw-text-slate-900">{filteredLogs.length}</span></div>
                        <div className="tw-flex tw-gap-2">
                            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-text-sm tw-font-bold disabled:tw-opacity-50">Өмнөх</button>
                            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-text-sm tw-font-bold disabled:tw-opacity-50">Дараах</button>
                        </div>
                    </div>
                )}
            </div>

            {/* ЧӨЛӨӨ БҮРТГЭХ MODAL */}
            {isLeaveModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-p-4">
                    <div className="tw-absolute tw-inset-0 tw-bg-slate-900/40 tw-backdrop-blur-sm" onClick={() => setIsLeaveModalOpen(false)}></div>
                    <div className="tw-relative tw-w-full tw-max-w-lg tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden tw-animate-fade-in-up">
                        <div className="tw-px-6 tw-py-4 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center">
                            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0 tw-flex tw-items-center tw-gap-2"><FiFileText className="tw-text-indigo-500"/> Зөвшөөрөлтэй эзгүй байх</h3>
                            <button onClick={() => setIsLeaveModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500"><FiX size={20} /></button>
                        </div>
                        <form onSubmit={handleSaveLeave} className="tw-p-6">
                            <div className="tw-flex tw-flex-col tw-gap-4">
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-bold tw-text-slate-600 tw-mb-1.5">Ажилтан сонгох <span className="tw-text-rose-500">*</span></label>
                                    <select required value={leaveForm.emp_id} onChange={(e) => setLeaveForm({...leaveForm, emp_id: e.target.value})} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-bold tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500">
                                        <option value="">-- Ажилтан сонгоно уу --</option>
                                        {employees.map(emp => (
                                            <option key={emp.emp_id} value={emp.emp_id}>{emp.lastName?.charAt(0)}. {emp.firstName || emp.name} ({emp.department})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div>
                                        <label className="tw-block tw-text-xs tw-font-bold tw-text-slate-600 tw-mb-1.5">Эхлэх огноо <span className="tw-text-rose-500">*</span></label>
                                        <input type="date" required value={leaveForm.startDate} onChange={(e) => setLeaveForm({...leaveForm, startDate: e.target.value})} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-bold tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-xs tw-font-bold tw-text-slate-600 tw-mb-1.5">Дуусах огноо <span className="tw-text-rose-500">*</span></label>
                                        <input type="date" required value={leaveForm.endDate} onChange={(e) => setLeaveForm({...leaveForm, endDate: e.target.value})} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-bold tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500" />
                                    </div>
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-bold tw-text-slate-600 tw-mb-1.5">Төрөл</label>
                                    <select value={leaveForm.leaveType} onChange={(e) => setLeaveForm({...leaveForm, leaveType: e.target.value})} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-bold tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500">
                                        <option value="leave">Чөлөө</option>
                                        <option value="annual_leave">Ээлжийн амралт</option>
                                        <option value="business_trip">Томилолт</option>
                                        <option value="sick">Өвчтэй (Эмнэлгийн магадлагаатай)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-bold tw-text-slate-600 tw-mb-1.5">Тайлбар / Шалтгаан</label>
                                    <textarea rows="2" placeholder="Жишээ нь: Гадаад руу хуралд явав..." value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-indigo-500 tw-resize-none"></textarea>
                                </div>
                            </div>
                            <button type="submit" disabled={isSaving} className="tw-w-full tw-mt-6 tw-py-3 tw-bg-indigo-600 tw-text-white tw-font-bold tw-rounded-xl hover:tw-bg-indigo-700">
                                {isSaving ? "Бүртгэж байна..." : "Баталгаажуулах"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* ТӨЛӨВ ЗАСАХ MODAL */}
            {isEditModalOpen && selectedEmployee && (
                <div className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-p-4">
                    <div className="tw-absolute tw-inset-0 tw-bg-slate-900/40 tw-backdrop-blur-sm" onClick={() => setIsEditModalOpen(false)}></div>
                    <div className="tw-relative tw-w-full tw-max-w-sm tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden tw-animate-fade-in-up">
                        <div className="tw-px-6 tw-py-4 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center">
                            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0">Төлөв өөрчлөх</h3>
                            <button onClick={() => setIsEditModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500"><FiX size={20} /></button>
                        </div>
                        <div className="tw-p-6">
                            <p className="tw-text-sm tw-text-slate-600 tw-mb-4">Та <b>{selectedEmployee.name}</b> ажилтны <b>{dateToString(selectedDate)}</b>-ны өдрийн төлөвийг өөрчлөх гэж байна.</p>
                            <select value={editStatusValue} onChange={(e) => setEditStatusValue(e.target.value)} className="tw-w-full tw-px-4 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-bold tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500">
                                <option value="ontime">Хэвийн ирсэн</option>
                                <option value="late">Хоцорсон</option>
                                <option value="absent">Ажилд ирээгүй</option>
                                <option value="leave">Чөлөө</option>
                                <option value="annual_leave">Ээлжийн амралт</option>
                                <option value="business_trip">Томилолт</option>
                                <option value="sick">Өвчтэй</option>
                            </select>
                            <button onClick={handleSaveStatus} disabled={isSaving} className="tw-w-full tw-mt-6 tw-py-3 tw-bg-blue-600 tw-text-white tw-font-bold tw-rounded-xl hover:tw-bg-blue-700">
                                {isSaving ? "Хадгалж байна..." : "Хадгалах"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ДЭЛГЭРЭНГҮЙ ХАРАХ MODAL */}
            {isViewModalOpen && selectedEmployee && (
                <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4 sm:tw-p-6">
                    <div className="tw-absolute tw-inset-0 tw-bg-slate-900/50 tw-backdrop-blur-sm" onClick={() => setIsViewModalOpen(false)}></div>
                    <div className="tw-relative tw-w-full tw-max-w-2xl tw-bg-slate-50 tw-rounded-3xl tw-shadow-2xl tw-overflow-hidden tw-animate-fade-in-up tw-flex tw-flex-col tw-max-h-[90vh]">
                        <div className="tw-bg-white tw-px-6 tw-py-4 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center tw-sticky tw-top-0 tw-z-10">
                            <h3 className="tw-text-lg tw-font-black tw-text-slate-800 tw-m-0">Дэлгэрэнгүй мэдээлэл</h3>
                            <button onClick={() => setIsViewModalOpen(false)} className="tw-p-2 tw-bg-slate-100 hover:tw-bg-rose-100 tw-text-slate-500 hover:tw-text-rose-600 tw-rounded-xl"><FiX size={20} /></button>
                        </div>
                        <div className="tw-p-6 tw-overflow-y-auto">
                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-6 tw-flex tw-items-center tw-gap-6 tw-mb-6 tw-shadow-sm">
                                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-font-black tw-text-2xl">{(selectedEmployee.name)?.charAt(0)}</div>
                                <div>
                                    <h2 className="tw-text-xl tw-font-black tw-text-slate-800 tw-m-0">{selectedEmployee.name}</h2>
                                    <p className="tw-text-sm tw-font-bold tw-text-slate-500 tw-mt-1">{selectedEmployee.department} <span className="tw-mx-1">•</span> <span className="tw-text-blue-600">{selectedEmployee.position || "Ажилтан"}</span></p>
                                </div>
                            </div>
                            
                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-6 tw-mb-6 tw-shadow-sm">
                                <h4 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-4 tw-border-b tw-border-slate-100 tw-pb-2">Үндсэн мэдээлэл</h4>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div><div className="tw-text-[10px] tw-text-slate-400 tw-uppercase">Ажилтны ID</div><div className="tw-font-bold tw-text-slate-700 tw-text-sm">{selectedEmployee.emp_id}</div></div>
                                    <div><div className="tw-text-[10px] tw-text-slate-400 tw-uppercase">Ажиллах хэлбэр</div><div className="tw-font-bold tw-text-slate-700 tw-text-sm">{selectedEmployee.employmentType || "-"}</div></div>
                                    <div><div className="tw-text-[10px] tw-text-slate-400 tw-uppercase">Утас</div><div className="tw-font-bold tw-text-slate-700 tw-text-sm">{selectedEmployee.phone || "-"}</div></div>
                                    <div><div className="tw-text-[10px] tw-text-slate-400 tw-uppercase">И-мэйл</div><div className="tw-font-bold tw-text-slate-700 tw-text-sm tw-break-all">{selectedEmployee.email || "-"}</div></div>
                                </div>
                            </div>

                            <div>
                                <h4 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-3">Сүүлийн үеийн ирц (7 хоног)</h4>
                                <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-overflow-hidden">
                                    {isLoadingAttendance ? <div className="tw-p-10 tw-text-center tw-text-blue-500">Уншиж байна...</div> : employeeAttendance.length === 0 ? <div className="tw-p-10 tw-text-center tw-text-slate-400">Мэдээлэл олдсонгүй.</div> : (
                                        <table className="tw-w-full tw-text-left tw-text-sm">
                                            <thead className="tw-bg-slate-50/80 tw-border-b tw-border-slate-100">
                                                <tr><th className="tw-px-5 tw-py-3 tw-font-bold tw-text-slate-500">Огноо</th><th className="tw-px-5 tw-py-3 tw-font-bold tw-text-slate-500">Ирсэн</th><th className="tw-px-5 tw-py-3 tw-font-bold tw-text-slate-500">Гарсан</th><th className="tw-px-5 tw-py-3 tw-font-bold tw-text-slate-500">Төлөв</th></tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-slate-100">
                                                {employeeAttendance.map((log) => {
                                                    const statusUI = getStatusUI(log.status);
                                                    return (
                                                        <tr key={log.id} className="hover:tw-bg-slate-50">
                                                            <td className="tw-px-5 tw-py-3 tw-font-bold tw-text-slate-700">{log.date}</td>
                                                            <td className="tw-px-5 tw-py-3 tw-font-medium tw-text-emerald-600">{formatTimeOnly(log.first_in)}</td>
                                                            <td className="tw-px-5 tw-py-3 tw-font-medium tw-text-rose-600">{formatTimeOnly(log.last_out)}</td>
                                                            <td className="tw-px-5 tw-py-3"><span className={`tw-px-2 tw-py-1 tw-rounded-md tw-text-[10px] tw-font-bold ${statusUI.color}`}>{statusUI.text}</span></td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <style jsx="true">{`
                .custom-scrollbar::-webkit-scrollbar { height: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
            `}</style>
        </div>
    );
}